import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, Edit2, Check, AlertCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  useDocumentRequirements, 
  applyManualVerifications,
  ManualVerificationState 
} from '@/hooks/useDocumentRequirements';
import { ManualRequirementsChecklist } from './ManualRequirementsChecklist';
import { DocumentPreview } from './DocumentPreview';

interface DocumentVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  companyId: string;
  document?: any;
  onVerify: (params: {
    documentId: string;
    status: 'verified' | 'rejected';
    notes?: string;
    verifiedData?: any;
    manualVerifications?: ManualVerificationState;
  }) => Promise<any>;
}

export function DocumentVerificationDialog({
  open,
  onOpenChange,
  documentId,
  companyId,
  document,
  onVerify,
}: DocumentVerificationDialogProps) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, any>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [manualVerifications, setManualVerifications] = useState<ManualVerificationState>({});

  // Get requirements validation - merge top-level columns with extracted_data
  const mergedData = {
    ...document?.extracted_data,
    // Include top-level columns if extracted_data doesn't have them
    document_number: document?.extracted_data?.document_number || document?.document_number,
    issue_date: document?.extracted_data?.issue_date || document?.issue_date,
    expiry_date: document?.extracted_data?.expiry_date || document?.expiry_date,
    issuing_authority: document?.extracted_data?.issuing_authority || document?.issuing_authority,
    ...editedData
  };
  
  const baseValidation = useDocumentRequirements(document?.document_type || '', mergedData);
  const validation = applyManualVerifications(baseValidation, manualVerifications);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setNotes('');
      setEditedData({});
      setEditingField(null);
      setManualOverride(false);
      setManualVerifications({});
    }
  }, [open]);

  const formatFieldName = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      return `$${value.toLocaleString()}`;
    }
    return String(value);
  };

  const getConfidenceBadgeVariant = (confidence: number) => {
    if (confidence >= 0.8) return 'default';
    if (confidence >= 0.5) return 'secondary';
    return 'destructive';
  };

  const handleSubmit = async (decision: 'verified' | 'rejected') => {
    if (decision === 'rejected' && !notes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    // Validate if approving (allow manual override or warnings only)
    if (decision === 'verified' && !validation.isValid && !manualOverride) {
      toast.error('Cannot approve document with critical errors. Use manual override if you have verified the document manually.');
      return;
    }

    // Require notes for manual override
    if (manualOverride && !notes.trim()) {
      toast.error('Please provide notes explaining why requirements are being overridden');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use merged data that includes both top-level columns and extracted_data
      const verifiedData = mergedData;
      
      await onVerify({
        documentId,
        status: decision,
        notes: manualOverride 
          ? `[MANUAL OVERRIDE] ${notes}` 
          : notes || undefined,
        verifiedData,
        manualVerifications,
      });

      toast.success(`Document ${decision === 'verified' ? 'approved' : 'rejected'} successfully`);
      onOpenChange(false);
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify document');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle>Verify Document</DialogTitle>
          <DialogDescription>
            Review the document and extracted information side-by-side
          </DialogDescription>
        </DialogHeader>

        {/* Main Content - Split View */}
        <div className="grid grid-cols-2 gap-0 border-t relative" style={{ height: 'calc(95vh - 280px)' }}>
          {/* Left: Document Preview */}
          <div className="border-r h-full overflow-hidden">
            {document?.file_path && document?.file_name ? (
              <DocumentPreview filePath={document.file_path} fileName={document.file_name} />
            ) : (
              <div className="flex h-full items-center justify-center bg-muted/50">
                <p className="text-sm text-muted-foreground">No document available</p>
              </div>
            )}
          </div>

          {/* Right: Extracted Data & Validation */}
          <ScrollArea className="h-full">
            <div className="space-y-6 p-6">
              {/* Document Metadata */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">Document Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    <span className="font-medium">{document?.document_type || 'N/A'}</span>
                  </div>
                  <div>
                <span className="text-muted-foreground">Uploaded:</span>{' '}
                <span className="font-medium">
                  {document?.created_at
                    ? new Date(document.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">File:</span>{' '}
                    <span className="font-medium truncate">{document?.file_name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Requirements Validation */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  Requirements Check
                  {!validation.isValid && (
                    <Badge variant="destructive" className="text-xs">
                      {validation.errors.length + validation.checks.filter(c => c.requiresManualReview && !c.manuallyVerified).length} pending
                    </Badge>
                  )}
                </h3>
                <ManualRequirementsChecklist
                  checks={validation.checks}
                  manualState={manualVerifications}
                  onManualStateChange={setManualVerifications}
                  disabled={isSubmitting}
                />
              </div>

              <Separator />

              {/* Extraction Confidence Warning */}
              {document?.extraction_confidence && document.extraction_confidence < 0.7 && (
                <div className="flex items-start gap-3 rounded-lg border border-warning bg-warning/10 p-3">
                  <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-warning">Low Confidence Extraction</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      AI extraction confidence is {Math.round(document.extraction_confidence * 100)}%. 
                      Please review all fields carefully.
                    </p>
                  </div>
                </div>
              )}

              {/* Extracted Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">Extracted Information</h3>
                {(() => {
                  // Build complete data view from both extracted_data and top-level columns
                  const displayData: Record<string, any> = {};
                  
                  // Add top-level columns first
                  if (document?.document_number) displayData.document_number = document.document_number;
                  if (document?.issue_date) displayData.issue_date = document.issue_date;
                  if (document?.expiry_date) displayData.expiry_date = document.expiry_date;
                  if (document?.issuing_authority) displayData.issuing_authority = document.issuing_authority;
                  
                  // Overlay extracted_data (takes precedence)
                  if (document?.extracted_data && typeof document.extracted_data === 'object') {
                    Object.assign(displayData, document.extracted_data);
                  }
                  
                  const hasData = Object.keys(displayData).length > 0;
                  
                  return hasData ? (
                    <div className="space-y-3 rounded-lg border bg-card p-4">
                      {Object.entries(displayData).map(([key, value]) => {
                        const isEditing = editingField === key;
                        const displayValue = editedData[key] !== undefined ? editedData[key] : value;
                        const fieldConfidence = document?.extracted_data?.field_confidence?.[key];

                        return (
                          <div key={key} className="flex items-start justify-between gap-2 pb-3 border-b last:border-0 last:pb-0">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Label className="text-xs text-muted-foreground">
                                  {formatFieldName(key)}
                                </Label>
                                {fieldConfidence && (
                                  <Badge
                                    variant={getConfidenceBadgeVariant(fieldConfidence)}
                                    className="text-xs"
                                  >
                                    {Math.round(fieldConfidence * 100)}%
                                  </Badge>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editedData[key] || ''}
                                    onChange={(e) =>
                                      setEditedData((prev) => ({ ...prev, [key]: e.target.value }))
                                    }
                                    className="h-8"
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingField(null)}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-sm font-medium break-words">
                                  {formatFieldValue(displayValue)}
                                </p>
                              )}
                            </div>
                            {!isEditing && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingField(key)}
                                className="flex-shrink-0"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-warning bg-warning/10 p-4">
                      <p className="text-sm text-warning font-medium">No data extracted</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Manual verification required. Please review the document and enter information manually.
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Verification Decision */}
        <div className="space-y-4 px-6">
          <h3 className="text-sm font-semibold">Verification Decision</h3>
          
          {!validation.isValid && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive bg-destructive/10 p-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Critical Requirements Not Met</p>
                <p className="text-xs text-muted-foreground mt-1">
                  This document has {validation.errors.length} critical requirement{validation.errors.length !== 1 ? 's' : ''} that must be resolved:
                </p>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  {validation.errors.map((error, idx) => (
                    <li key={idx}>{error.message}</li>
                  ))}
                </ul>
                <div className="mt-3 pt-3 border-t border-destructive/20">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={manualOverride}
                      onChange={(e) => setManualOverride(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="font-medium">Manual Override</span>
                    <span className="text-muted-foreground">(I have verified this document manually and it meets requirements)</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {validation.warnings.length > 0 && validation.isValid && (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-500 bg-yellow-500/10 p-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-700">Warnings</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Some fields could not be automatically verified. Manual review recommended:
                </p>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  {validation.warnings.map((warning, idx) => (
                    <li key={idx}>{warning.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="notes">Verification Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this verification decision..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <div className="flex items-center justify-between w-full gap-3">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            <div className="flex gap-3">
              <Button
                onClick={() => handleSubmit('rejected')}
                disabled={isSubmitting}
                variant="destructive"
              >
                <XCircle className="mr-2 h-4 w-4" />
                {isSubmitting ? 'Rejecting...' : 'Reject Document'}
              </Button>
              
              <Button
                onClick={() => handleSubmit('verified')}
                disabled={isSubmitting || (!validation.isValid && !manualOverride)}
                variant="default"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {isSubmitting 
                  ? 'Approving...' 
                  : manualOverride 
                    ? 'Approve (Manual Override)' 
                    : 'Approve Document'
                }
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
